import { isPresentationMode } from 'env';
import { omit } from 'lodash';
import { api, batchApi, handleError, scenarioApi } from '@ardoq/api';
import {
  ArdoqError,
  isArdoqError,
  isTruthy,
  toArdoqError,
} from '@ardoq/common-helpers';
import {
  addReferences,
  batchLoadReferences,
  batchRemoveReferences,
  createReference,
  createReferences,
  createReferencesFailed,
  deleteReference,
  flipReference,
  lockReferences,
  deleteReferences,
  deleteReferencesSuccess,
  removeReferences,
  resetReferences,
  reverseReference,
  reverseReferences,
  saveReferencesAction,
  saveReferencesFailed,
  saveReferencesSuccess,
  selectReference,
  unlockReferences,
} from './ReferenceActions';
import {
  action$,
  collectRoutines,
  dispatchAction,
  routine,
  extractPayload,
  ofType,
  spreadFilter,
  spreadTap,
} from '@ardoq/rxbeach';
import { filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { launchDeleteComponentsAndReferencesConfirmationDialogByIds } from 'components/Dialogs/confirmDeletion/confirmDeletion';
import { externallyManagedNotice$ } from 'externallyManaged/externallyManagedNotice$';
import {
  ResolvedReverseReference,
  reverseResolvedReferences,
} from 'components/Dialogs/reverseReferences/reverseReferences';
import { selectNodeInPresentationMode } from 'presentation/slideNavigator/actions';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { components$ } from 'streams/components/components$';
import { persistentWorkspaces$ as workspaces$ } from 'streams/workspaces/workspaces$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { contextInterface } from 'modelInterface/contextInterface';
import { clearReference, setReference } from 'streams/context/ContextActions';
import {
  APIComponentAttributes,
  APIReferenceAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
  ProvenanceSource,
  ResourceType,
} from '@ardoq/api-types';
import { context$ } from 'streams/context/context$';
import {
  getConnectedWorkspaceIds as getConnectedWorkspaceIdsFromMap,
  linkedWorkspaces$,
} from 'streams/linkedWorkspaces$';
import { withLatestFrom } from 'rxjs/operators';
import {
  notifyReferenceLocked,
  notifyReferenceUnlocked,
} from './ReferenceActions';
import { workspacesClosed$ } from 'streams/context/context$';
import { references$ } from './references$';
import { activeScenario$ } from 'streams/activeScenario/activeScenario$';
import { toIds } from '@ardoq/core';
import * as referenceActions from './ReferenceActions';
import { isEmpty } from 'lodash';
import { advancedSearchApi } from '@ardoq/api';
import { logError, logWarn } from '@ardoq/logging';
import { dispatchWebsocketEvent, websocket$ } from 'sync/websocket$';
import { ArdoqEvent, EventType } from 'sync/types';
import {
  ardoqEventOperations,
  shouldProcessEventForBranch,
} from 'sync/ardoqEventOperations';
import { activeBranchId$ } from 'streams/activeScenario/activeBranchId$';
import { dataInterface } from '@ardoq/data-interface';
import { updateLinkedWorkspaces } from 'models/actions';
import { componentInterface } from '@ardoq/component-interface';
import { isViewpointMode$ } from 'traversals/loadedGraph$';
import { isNotHandledByViewpointModeRoutines } from 'loadedState/isHandledByViewpointModeRoutine';
import { createReferencesSucceeded } from 'componentOverview2025/referencesTab/referencesTabActions';
import { loadedCurrentUser$ } from 'streams/currentUser/currentUser$';
import { selectedModule$ } from 'appContainer/selectedModule$';
import { AppModules } from 'appContainer/types';

const selectReferenceHandler = routine(
  ofType(selectReference),
  extractPayload(),
  tap(({ cid }) => {
    if (isPresentationMode()) {
      if (referenceInterface.isReference(cid)) {
        dispatchAction(selectNodeInPresentationMode(cid));
      }
      if (!contextInterface.isExploreMode()) {
        return;
      }
    }

    if (referenceInterface.isReference(cid)) {
      dispatchAction(setReference(cid));
    } else {
      dispatchAction(clearReference());
    }
  })
);

const handleDeleteReference = routine(
  ofType(deleteReference),
  extractPayload(),
  withLatestFrom(externallyManagedNotice$),
  tap(([id, { isSuppressed: isExternallyManagedNoticeSuppressed }]) => {
    launchDeleteComponentsAndReferencesConfirmationDialogByIds({
      referenceIds: [id],
      isExternallyManagedNoticeSuppressed,
    });
  })
);

const toLockUpdates = (
  referenceIds: ArdoqId[],
  lock: ArdoqId | null,
  byId: Record<string, APIReferenceAttributes>
): APIReferenceAttributes[] =>
  referenceIds
    .map(_id => byId[_id])
    .filter(isTruthy)
    .map(ref => ({ ...ref, lock }));

const handleLockReferences = routine(
  ofType(lockReferences),
  extractPayload(),
  withLatestFrom(references$, activeScenario$),
  map(([{ referenceIds, userId }, currentReferences, { scenarioId }]) => ({
    updates: toLockUpdates(referenceIds, userId, currentReferences.byId),
    scenarioId,
  })),
  switchMap(({ updates, scenarioId }) =>
    doUpdateReferences(updates, scenarioId)
  ),
  handleError(),
  tap(results =>
    results.forEach(ref => dispatchAction(notifyReferenceLocked(ref._id)))
  )
);

const handleUnlockReferences = routine(
  ofType(unlockReferences),
  extractPayload(),
  withLatestFrom(references$, activeScenario$),
  map(([referenceIds, currentReferences, { scenarioId }]) => ({
    updates: toLockUpdates(referenceIds, null, currentReferences.byId),
    scenarioId,
  })),
  switchMap(({ updates, scenarioId }) =>
    doUpdateReferences(updates, scenarioId)
  ),
  handleError(),
  tap(results =>
    results.forEach(ref => dispatchAction(notifyReferenceUnlocked(ref._id)))
  )
);

const handleReverseReference = routine(
  ofType(reverseReference),
  extractPayload(),
  tap(id => dispatchAction(reverseReferences([id])))
);

const toResolvedReverseReference = (
  reference: APIReferenceAttributes,
  componentsById: Record<ArdoqId, APIComponentAttributes>,
  workspacesById: Record<ArdoqId, APIWorkspaceAttributes>
): ResolvedReverseReference => ({
  reference,
  referenceTypeName: referenceInterface.getModelType(reference._id)?.name,
  targetWsReferenceTypes: reference.targetWorkspace
    ? workspaceInterface.getReferenceTypes(reference.targetWorkspace)
    : [],
  sourceComponentName: componentsById[reference.source]?.name ?? null,
  targetComponentName: componentsById[reference.target]?.name ?? null,
  sourceWorkspaceName: workspacesById[reference.rootWorkspace]?.name ?? null,
  targetWorkspaceData: workspacesById[reference.targetWorkspace] ?? null,
});

const handleReverseReferences = routine(
  ofType(reverseReferences),
  extractPayload(),
  withLatestFrom(references$, components$, workspaces$, i18nLocale$),
  mergeMap(payload =>
    of(payload).pipe(
      tap(
        ([
          referenceIds,
          currentReferences,
          currentComponents,
          currentWorkspaces,
          i18nLocale,
        ]) => {
          const resolved = referenceIds
            .map(id => currentReferences.byId[id])
            .filter(isTruthy)
            .map(reference =>
              toResolvedReverseReference(
                reference,
                currentComponents.byId,
                currentWorkspaces.byId
              )
            );
          reverseResolvedReferences(resolved, i18nLocale);
        }
      ),
      handleError()
    )
  )
);

const handleFlipReference = routine(
  ofType(flipReference),
  extractPayload(),
  withLatestFrom(activeScenario$),
  switchMap(([flippedAttributes, { scenarioId }]) =>
    doUpdateReferences([flippedAttributes], scenarioId)
  ),
  handleError()
);

const handleBatchLoadReferences = routine(
  ofType(batchLoadReferences),
  extractPayload(),
  withLatestFrom(context$, linkedWorkspaces$, references$),
  spreadTap((references, context, linkedWorkspaces, currentReferences) => {
    const connectedWorkspaceIds = getConnectedWorkspaceIdsFromMap(
      context.workspaceIds,
      linkedWorkspaces
    );

    const referencesToAdd = references.filter(
      referenceAttributes =>
        connectedWorkspaceIds.has(referenceAttributes.rootWorkspace) &&
        !currentReferences.byId[referenceAttributes._id]
    );

    dispatchAction(addReferences({ references: referencesToAdd }));
  })
);

const handleBatchRemoveReferences = routine(
  ofType(batchRemoveReferences),
  extractPayload(),
  tap(ids => {
    dispatchAction(removeReferences({ ids }));
  })
);

const batchUpdateReferences = async (
  updateBodies: APIReferenceAttributes[],
  provenanceSource?: ProvenanceSource
) => {
  const response = await batchApi.execute(
    {
      options: { includeEntities: true },
      references: { update: updateBodies },
    },
    provenanceSource
  );

  if (isArdoqError(response)) {
    api.logErrorIfNeeded(response);
    return response;
  }

  const updatedMap = response.references.updated ?? {};
  return updateBodies.map(({ _id }) => updatedMap[_id]);
};

const createReferencesInScenario = async (
  referenceAttributes: Partial<APIReferenceAttributes>[],
  scenarioId: string
): Promise<APIReferenceAttributes[]> => {
  const results: APIReferenceAttributes[] = [];
  for (const attributes of referenceAttributes) {
    const result = await scenarioApi.createReference(
      omit(attributes, '_id'),
      scenarioId
    );
    if (isArdoqError(result)) {
      api.logErrorIfNeeded(result);
      continue;
    }
    results.push(result);
  }
  return results;
};

const batchCreateReferences = async (
  attributes: Partial<APIReferenceAttributes>[],
  provenanceSource?: ProvenanceSource
) => {
  const data = attributes.map(attrs => {
    const { _id: batchId, ...noIdAttrs } = attrs;
    return {
      ...noIdAttrs,
      batchId,
    };
  });
  const response = await batchApi.execute(
    {
      options: { includeEntities: true },
      references: {
        create: data,
      },
    },
    provenanceSource
  );

  if (isArdoqError(response)) {
    return response;
  }

  const resultMap = response.references.created ?? {};
  const persistedEntities = data.map(
    ({ batchId }) => resultMap[response.references.ids[batchId as string]]
  );
  return persistedEntities;
};

const doCreateReferences = async (
  referenceAttributes: Partial<APIReferenceAttributes>[],
  scenarioId: string | null,
  provenanceSource?: ProvenanceSource
): Promise<APIReferenceAttributes[]> => {
  if (scenarioId) {
    return createReferencesInScenario(referenceAttributes, scenarioId);
  }
  const result = await batchCreateReferences(
    referenceAttributes,
    provenanceSource
  );
  if (isArdoqError(result)) {
    api.logErrorIfNeeded(result);
    return [];
  }
  return result;
};

const handleCreateReference = routine(
  ofType(createReference),
  extractPayload(),
  withLatestFrom(activeScenario$),
  switchMap(([{ attributes: reference, provenanceSource }, { scenarioId }]) =>
    doCreateReferences([reference], scenarioId, provenanceSource)
  ),
  handleError()
);

const shouldRemoveReference = (
  ref: APIReferenceAttributes,
  closedWorkspaceIdsSet: Set<string>,
  openWorkspaceIdsSet: Set<string>
): boolean => {
  const rootInClosing = closedWorkspaceIdsSet.has(ref.rootWorkspace);
  const targetInClosing = closedWorkspaceIdsSet.has(ref.targetWorkspace);
  const rootStaysOpen = openWorkspaceIdsSet.has(ref.rootWorkspace);
  const targetStaysOpen = openWorkspaceIdsSet.has(ref.targetWorkspace);

  return (
    (rootInClosing && targetInClosing) ||
    (rootInClosing && !targetStaysOpen) ||
    (targetInClosing && !rootStaysOpen)
  );
};

const handleWorkspacesClosed = workspacesClosed$.pipe(
  withLatestFrom(references$),
  tap(([{ workspaceIds, closed }, currentReferences]) => {
    if (workspaceIds.length === 0) {
      dispatchAction(resetReferences());
      return;
    }

    const openWorkspaceIdsSet = new Set(workspaceIds);
    const closedWorkspaceIdsSet = new Set(closed);
    const toRemove = currentReferences.models.filter(ref =>
      shouldRemoveReference(ref, closedWorkspaceIdsSet, openWorkspaceIdsSet)
    );

    if (toRemove.length > 0) {
      dispatchAction(removeReferences({ ids: toIds(toRemove) }));
    }
  })
);

const handleCreateReferences = routine(
  ofType(createReferences),
  extractPayload(),
  withLatestFrom(activeScenario$),
  tap(async ([{ referenceAttributes }, { scenarioId }]) => {
    try {
      const persistedEntities = await doCreateReferences(
        referenceAttributes,
        scenarioId
      );
      dispatchAction(
        createReferencesSucceeded({
          createdReferences: persistedEntities,
          createdReferenceIds: persistedEntities.map(ref => ref._id),
          createdCopComponentIds: [],
        })
      );
    } catch (error) {
      dispatchAction(
        createReferencesFailed(
          toArdoqError({
            error: error instanceof Error ? error : new Error(String(error)),
          })
        )
      );
    }
  })
);

const updateReferencesInScenario = async (
  attributes: APIReferenceAttributes[],
  scenarioId: string
): Promise<APIReferenceAttributes[]> => {
  const results: APIReferenceAttributes[] = [];
  for (const attrs of attributes) {
    const result = await scenarioApi.updateReference(attrs, scenarioId);
    if (isArdoqError(result)) {
      api.logErrorIfNeeded(result);
      continue;
    }
    results.push(result);
  }
  return results;
};

const doUpdateReferences = async (
  attributes: APIReferenceAttributes[],
  scenarioId: string | null,
  provenanceSource?: ProvenanceSource
): Promise<APIReferenceAttributes[] | ArdoqError> => {
  const updated = scenarioId
    ? await updateReferencesInScenario(attributes, scenarioId)
    : await batchUpdateReferences(attributes, provenanceSource);
  if (isArdoqError(updated)) return updated;
  return updated;
};

const handleSaveReferences = routine(
  ofType(saveReferencesAction),
  extractPayload(),
  withLatestFrom(activeScenario$),
  tap(async ([{ attributes, provenanceSource }, { scenarioId }]) => {
    try {
      const result = await doUpdateReferences(
        attributes,
        scenarioId,
        provenanceSource
      );
      if (isArdoqError(result)) {
        dispatchAction(saveReferencesFailed(result));
        return;
      }
      result
        .filter(isTruthy)
        .forEach(reference =>
          dispatchAction(referenceActions.updateReference(reference))
        );
      dispatchAction(saveReferencesSuccess(result));
    } catch (error) {
      dispatchAction(
        saveReferencesFailed(
          toArdoqError({
            error: error instanceof Error ? error : new Error(String(error)),
          })
        )
      );
    }
  })
);

const deleteReferencesInScenario = async (
  referenceIds: ArdoqId[],
  scenarioId: string
): Promise<ArdoqId[]> => {
  const deleted: ArdoqId[] = [];
  for (const id of referenceIds) {
    const result = await scenarioApi.deleteReference(id, scenarioId);
    if (isArdoqError(result)) {
      api.logErrorIfNeeded(result);
      continue;
    }
    deleted.push(id);
  }
  return deleted;
};

const batchDeleteReferences = async (
  referenceIds: ArdoqId[],
  provenanceSource?: ProvenanceSource
): Promise<ArdoqId[] | ArdoqError> => {
  const result = await batchApi.execute(
    { references: { delete: referenceIds } },
    provenanceSource
  );
  if (isArdoqError(result)) return result;
  return referenceIds;
};

const doDeleteReferences = async (
  referenceIds: ArdoqId[],
  scenarioId: string | null,
  provenanceSource?: ProvenanceSource
): Promise<ArdoqId[]> => {
  if (scenarioId) {
    return deleteReferencesInScenario(referenceIds, scenarioId);
  }
  const result = await batchDeleteReferences(referenceIds, provenanceSource);
  if (isArdoqError(result)) {
    api.logErrorIfNeeded(result);
    return [];
  }
  return result;
};

const handleDeleteReferences = routine(
  ofType(deleteReferences),
  extractPayload(),
  withLatestFrom(activeScenario$),
  tap(async ([{ referenceIds, provenanceSource }, { scenarioId }]) => {
    if (referenceIds.length === 0) return;

    const deleted = await doDeleteReferences(
      referenceIds,
      scenarioId,
      provenanceSource
    );
    dispatchAction(removeReferences({ ids: deleted }));
    dispatchAction(deleteReferencesSuccess(deleted));
  })
);

const resolveReferenceSourceAndTarget = ({
  source,
  target,
}: APIReferenceAttributes) => [
  {
    id: source,
    component: componentInterface.get(source),
    kind: 'source',
  },
  {
    id: target,
    component: componentInterface.get(target),
    kind: 'target',
  },
];

const resolveDanglingReference = async (
  event: ArdoqEvent<APIReferenceAttributes>
): Promise<boolean> => {
  const missingComponentIds = resolveReferenceSourceAndTarget(event.data)
    .filter(({ component }) => !component)
    .map(({ id }) => id);

  if (isEmpty(missingComponentIds)) {
    return true;
  }

  const results = await advancedSearchApi.scopeData({
    componentIds: missingComponentIds,
    referenceIds: [event.data._id],
  });

  if (isArdoqError(results)) {
    logError(Error('Failed to resolve dangling reference'));
    return false;
  }

  dataInterface.addScopeData(results);
  dispatchAction(updateLinkedWorkspaces({ references: [event.data] }));
  return true;
};

const handleReferenceEvent = async (
  event: ArdoqEvent<APIReferenceAttributes>
) => {
  const eventType = event['event-type'];
  switch (eventType) {
    case EventType.DELETE:
      break;
    case EventType.CREATE: {
      const resolved = await resolveDanglingReference(event);
      if (!resolved) return;
      dispatchAction(
        referenceActions.addReferences({ references: [event.data] })
      );
      break;
    }
    case EventType.UPDATE: {
      const resolved = await resolveDanglingReference(event);
      if (!resolved) return;
      dispatchAction(referenceActions.updateReference(event.data));
      break;
    }
    default:
      logWarn(new Error('unknown event-type'), eventType);
  }
};

const handleReferencesRoutine = websocket$.pipe(
  withLatestFrom(isViewpointMode$),
  spreadFilter(isNotHandledByViewpointModeRoutines),
  filter(ardoqEventOperations.isReferenceEvent),
  withLatestFrom(activeBranchId$),
  spreadFilter(shouldProcessEventForBranch),
  tap(handleReferenceEvent)
);

type ReferencesWithContext = {
  references: APIReferenceAttributes[];
  workspaceIds: string[];
  user: { name: string; id: ArdoqId };
  org: { label: string; _id: ArdoqId };
  selectedModule: AppModules;
};

const toReferencesWithContext = ([
  references,
  context,
  currentUser,
  selectedModule,
]: [
  { createdReferences: APIReferenceAttributes[] },
  { workspaceIds: string[] },
  { name: string; _id: string; organization: { label: string; _id: string } },
  { selectedModule: AppModules },
]): ReferencesWithContext => ({
  references: references.createdReferences,
  workspaceIds: context.workspaceIds,
  user: { name: currentUser.name, id: currentUser._id },
  org: {
    label: currentUser.organization.label,
    _id: currentUser.organization._id,
  },
  selectedModule: selectedModule.selectedModule,
});

const isVisualizationView = ({ selectedModule }: ReferencesWithContext) =>
  selectedModule === AppModules.WORKSPACES;

const isNotSubscribedToWebsocketEvents = ({
  references,
  workspaceIds,
}: ReferencesWithContext) =>
  references.some(reference => !workspaceIds.includes(reference.rootWorkspace));

const dispatchSyntheticWebsocketEvent = ({
  workspaceIds,
  references,
  user,
  org,
}: ReferencesWithContext) => {
  references
    .filter(reference => !workspaceIds.includes(reference.rootWorkspace))
    .forEach(reference => {
      const syntheticEvent: ArdoqEvent<APIReferenceAttributes> = {
        token: '',
        'resource-type': ResourceType.REFERENCE,
        'event-type': EventType.CREATE,
        data: reference,
        user,
        org,
      };
      dispatchWebsocketEvent(syntheticEvent);
    });
};

const handleReferenceCreatedInComponentOverview = action$.pipe(
  ofType(createReferencesSucceeded),
  extractPayload(),
  withLatestFrom(context$, loadedCurrentUser$, selectedModule$),
  map(toReferencesWithContext),
  filter(isVisualizationView),
  filter(isNotSubscribedToWebsocketEvents),
  tap(dispatchSyntheticWebsocketEvent)
);

export const routines = collectRoutines(
  selectReferenceHandler,
  handleDeleteReference,
  handleReverseReference,
  handleReverseReferences,
  handleFlipReference,
  handleLockReferences,
  handleUnlockReferences,
  handleBatchLoadReferences,
  handleBatchRemoveReferences,
  handleCreateReference,
  handleCreateReferences,
  handleSaveReferences,
  handleDeleteReferences,
  handleWorkspacesClosed,
  handleReferencesRoutine,
  handleReferenceCreatedInComponentOverview
);
